import React, { useEffect, useState } from 'react';
import { BookMarked } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getActiveSystem } from '../../config/system';
import {
  getAllowedSources,
  ALLOWED_SOURCES_CHANGED_EVENT,
} from '../../config/allowedSources';
import { PREFERENCES_APPLIED_EVENT } from '../../config/preferencesSync';

/**
 * The visibility half of allowed sources (issue #310, rule 2): when a reader's
 * saved set is narrowing the current results, the page SAYS SO and offers one
 * click to ignore it. A filter a reader cannot see is a filter they will
 * mistake for missing content — and on a reference site "the spell isn't
 * there" is the worst failure it can produce.
 *
 * Renders nothing when there is nothing to say: signed out, no set saved for
 * the active system, or the URL already carries an explicit book choice
 * (`?rulebook=` / `?rulebook_not=`), in which case the URL won and the saved
 * set is not applied at all (rule 1 — the server's `activeAllowedSources`
 * suppression, mirrored here so page and payload agree).
 *
 * The bypass is URL STATE, not hidden client state: "Show all sources" writes
 * `?sources=all`, which the server honours as an explicit choice, so the
 * bypassed view is shareable and resolves identically for whoever opens it.
 *
 * Used two ways: URL-driven on GenericList (`bypassed` from `?sources=`,
 * `onChange` writes the param) and state-driven on the generator pages, which
 * carry no book params in their page URL.
 */
const AllowedSourcesNotice = ({ bypassed, suppressed = false, onChange }) => {
  const { isAuthenticated } = useAuth();
  // The saved set is read from the local preferences mirror; re-read when this
  // browser edits it or a server sync arrives.
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(ALLOWED_SOURCES_CHANGED_EVENT, bump);
    window.addEventListener(PREFERENCES_APPLIED_EVENT, bump);
    return () => {
      window.removeEventListener(ALLOWED_SOURCES_CHANGED_EVENT, bump);
      window.removeEventListener(PREFERENCES_APPLIED_EVENT, bump);
    };
  }, []);

  const sources = isAuthenticated ? getAllowedSources(getActiveSystem()) : null;
  if (!sources || suppressed) return null;

  const count = sources.length;
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-dnd-blue/40 bg-dnd-blue/10 px-3 py-2 text-sm text-secondary"
      role="status"
      data-testid="allowed-sources-notice"
    >
      <BookMarked className="h-4 w-4 shrink-0" aria-hidden="true" />
      {bypassed ? (
        <>
          <span>
            Your allowed sources ({count} {count === 1 ? 'book' : 'books'}) are
            being ignored.
          </span>
          <button
            type="button"
            className="font-semibold underline hover:no-underline"
            onClick={() => onChange('')}
          >
            Re-apply
          </button>
        </>
      ) : (
        <>
          <span>
            Showing only your allowed sources ({count}{' '}
            {count === 1 ? 'book' : 'books'}).
          </span>
          <button
            type="button"
            className="font-semibold underline hover:no-underline"
            onClick={() => onChange('all')}
          >
            Show all sources
          </button>
        </>
      )}
    </div>
  );
};

export default AllowedSourcesNotice;
